import { z } from "zod";

/**
 * One Zod schema per CSV table. The header of the CSV and its schema are executable authority:
 * unknown columns, missing references, duplicate ids and bad enums fail the build.
 *
 * Adding a table: add a CSV under data/, a schema here, a key in TABLE_SCHEMAS, a typed accessor
 * in load.ts, and a row in docs/data.md. The `add-data-table` skill walks through this.
 */

const csvBool = z.enum(["0", "1"]).transform((v) => v === "1");
const csvInt = z
  .string()
  .regex(/^-?\d+$/, "expected integer")
  .transform((v) => Number.parseInt(v, 10));
const csvNumber = z
  .string()
  .regex(/^-?\d+(\.\d+)?$/, "expected number")
  .transform((v) => Number.parseFloat(v));
const csvId = z.string().regex(/^[a-z0-9_]+$/, "ids are lower_snake_case");
const csvColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "expected #rrggbb");
const csvOptionalId = z
  .string()
  .transform((v) => (v === "" ? null : v))
  .pipe(csvId.nullable());

const ConfigRowSchema = z.object({
  key: csvId,
  value: z.string(),
  description: z.string(),
});

export const EntityKindRowSchema = z.object({
  id: csvId,
  name_key: z.string().min(1),
  color: csvColor,
  scale: csvNumber,
  shape: z.enum(["box", "sphere", "cone"]),
  tap_reward_id: csvOptionalId,
});

export const CurrencyRowSchema = z.object({
  id: csvId,
  name_key: z.string().min(1),
  starting_config_key: csvId,
});

export const RewardRowSchema = z.object({
  id: csvId,
  currency: csvId,
  amount: csvInt,
  repeatable: csvBool,
  description: z.string(),
});

export const FeatureFlagRowSchema = z.object({
  key: csvId,
  default: csvBool,
  description: z.string(),
});

export const StringRowSchema = z.object({
  key: z.string().min(1),
  en: z.string(),
  description: z.string(),
});

export const TABLE_SCHEMAS = {
  config: ConfigRowSchema,
  entity_kinds: EntityKindRowSchema,
  currencies: CurrencyRowSchema,
  rewards: RewardRowSchema,
  feature_flags: FeatureFlagRowSchema,
  strings: StringRowSchema,
} as const;

export type TableName = keyof typeof TABLE_SCHEMAS;
export const TABLE_NAMES = Object.keys(TABLE_SCHEMAS) as TableName[];

/** Which column is the primary key of each table (must be unique). */
export const TABLE_PRIMARY_KEY: Record<TableName, string> = {
  config: "key",
  entity_kinds: "id",
  currencies: "id",
  rewards: "id",
  feature_flags: "key",
  strings: "key",
};

/** Typed config keys. Reading config goes through these so a typo fails typecheck, not runtime. */
export const ConfigSchema = z.object({
  sim_tick_hz: csvInt,
  max_entities: csvInt,
  entity_lifetime_ms: csvInt,
  camera_distance: csvNumber,
  camera_min_distance: csvNumber,
  camera_max_distance: csvNumber,
  ground_size: csvNumber,
  orientation: z.enum(["any", "portrait", "landscape"]),
  dpr_cap: csvNumber,
  autosave_debounce_ms: csvInt,
  starting_gold: csvInt,
  starting_gems: csvInt,
  store_url_ios: z.string(),
  store_url_android: z.string(),
  store_url_web: z.string(),
  support_email: z.string(),
  community_url: z.string(),
  youtube_url: z.string().url().startsWith("https://").or(z.literal("")),
  tiktok_url: z.string().url().startsWith("https://").or(z.literal("")),
  product_name: z.string().min(1),
  price_cents: csvInt,
  reservation_price_cents: csvInt,
  included_days: csvInt,
  price_currency: z.string().length(3),
  billing_interval: z.enum(["month"]),
  seat_cap: csvInt,
  max_devices: csvInt,
  device_removal_days: csvInt,
  device_stale_days: csvInt,
  waitlist_claim_hours: csvInt,
  admit_hold_hours: csvInt,
  inactivity_cancel_months: csvInt,
  paid_discord_invite_url: z.string(),
  og_tiers: z.string(),
});
export type GameConfig = z.infer<typeof ConfigSchema>;
